import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Card, Button, Form, Tab, Nav } from "react-bootstrap";
import { DEV_URL, rpost, rget } from "../../utils/requests";
import DataTable from "react-data-table-component";
import { useNavigate, useLocation } from "react-router-dom";
import Spinner from "../../components/Spinner";
import Table from "../../components/Table";
import { CompaniesDataTable } from "./CompaniesDataTable.tsx";
import Div from "../../components/Div";
import PageTitle from "../../components/PageTitle/index";
import PlusIcon from '@enerex/unified-ui-react/icons/AddIcon';
import { useApiFilters } from "hooks/useApiFilters";
import Text from "../../components/Text";
import { CompanyFilters } from "./CompanyFilter";


const CompaniesData = () => {
    const [extra, setExtra] = useState();
    const navigate = useNavigate();
    const location = useLocation();
    const [companyData, setCompanyData] = useState([]);
    const [companyDataCount, setCompanyDataCount] = useState([]);
    const [companyDataPage, setCompanyDataPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState('');
    const [companyDataPerPage, setCompanyDataPerPage] = useState(50);
    const [loadingCompanyData, setLoadingCompanyData] = useState(false);

    useEffect(() => {
        document.title = "DataHub - Companies";
    }, []);
    const searchParams = new URLSearchParams(location.search);
    // Ref to prevent infinite loops when syncing query params
    const isSyncingRef = useRef(false);
    // Ref to track when we're applying filters (to prevent sync effect from interfering)
    const isApplyingFiltersRef = useRef(false);

    // Initialize filters from query params
    const getInitialFilters = () => {
        // Map query param display values back to internal values
        const commodityParam = searchParams.get("Commodity") || searchParams.get("commodity") || '';
        let commodity = '0'; // Default to '0' (Both)
        if (commodityParam === 'Electric') {
            commodity = '1';
        } else if (commodityParam === 'Gas') {
            commodity = '2';
        }

        const search = searchParams.get("Search") || searchParams.get("search") || '';
        const state = searchParams.get("StateCode") || searchParams.get("stateCode") || '';
        const licenseType = searchParams.get("LicenseType") || searchParams.get("licenseType") || '';
        const licenseStatus = searchParams.get("LicenseStatus") || searchParams.get("licenseStatus") || '';
        return {
            commodity,
            search,
            state,
            licenseType,
            licenseStatus,
            followedByMe: searchParams.get('followedByMe') === 'true'
        };
    };

    let defaultPage = 1;
    if (searchParams.get("Page") || searchParams.get("page")) {
        defaultPage = parseInt(searchParams.get("Page") || searchParams.get("page") || "1");
    }

    const {
        filters,
        orderBy,
        ascendingOrder,
        limit,
        page,
        search,
        setPage,
        setSearch,
        hook,
        setFilters
    } = useApiFilters({
        page: defaultPage - 1, limit: 50, filters: getInitialFilters()
    });
    const paginationComponentOptions = {
        rowsPerPageText: "Rows per Page",
        rangeSeparatorText: "of",
        selectAllRowsItem: true,
        selectAllRowsItemText: "All",
    };

    const handleCompanyDataPageChange = (companyDataPage) => {
        setCompanyDataPage(companyDataPage);
    };

    const handleCompanyDataPerRowsChange = (companyDataPerPage) => {
        setCompanyDataPerPage(companyDataPerPage);
    };
    // Sync all filters to query params
    const syncFiltersToQueryParams = (filterData, pageNumber = null, preserveFilters = false) => {
        const searchParams = new URLSearchParams(location.search);
        let hasChanges = false;

        // Sync commodity
        // Only add Commodity param for '1' (Electric) or '2' (Gas)
        // Remove param for '0' (Both) - this is the default
        const currentCommodity = searchParams.get("Commodity") || '';
        // Normalize commodity value to string and handle various input types
        const commodityValue = String(filterData.commodity || '0').trim();

        if (commodityValue === '1' || commodityValue === '2') {
            // Map internal value to display value: '1' -> 'Electric', '2' -> 'Gas'
            const displayValue = commodityValue === '1' ? 'Electric' : 'Gas';
            if (currentCommodity !== displayValue) {
                searchParams.set("Commodity", displayValue);
                hasChanges = true;
            }
        } else {
            // For '0' (Both) or any other value, remove the param
            // Always delete if Commodity param exists in URL, regardless of its value
            if (searchParams.has("Commodity")) {
                searchParams.delete("Commodity");
                hasChanges = true;
            }
        }

        // Sync search
        const currentSearch = searchParams.get("Search") || '';
        // Ensure we handle null/undefined as empty string
        const newSearch = (filterData.search !== null && filterData.search !== undefined)
            ? String(filterData.search).trim()
            : '';
        // Only preserve search from URL if preserveFilters is true (page change scenario)
        // Otherwise, use the newSearch value (filter change scenario - should clear if empty)
        const searchToUse = preserveFilters ? (newSearch || currentSearch || '') : newSearch;
        if (searchToUse !== '') {
            if (currentSearch !== searchToUse) {
                searchParams.set("Search", searchToUse);
                hasChanges = true;
            }
        } else {
            // If searchToUse is empty, delete Search param from URL
            if (currentSearch !== '') {
                searchParams.delete("Search");
                hasChanges = true;
            }
        }

        // Sync state
        const currentState = searchParams.get("StateCode") || '';
        if (filterData.state && filterData.state !== '') {
            if (currentState !== filterData.state) {
                searchParams.set("StateCode", filterData.state);
                hasChanges = true;
            }
        } else {
            if (currentState !== '') {
                searchParams.delete("StateCode");
                hasChanges = true;
            }
        }

        // Sync licenseType
        const currentLicenseType = searchParams.get("LicenseType") || '';
        if (filterData.licenseType && filterData.licenseType !== '') {
            if (currentLicenseType !== filterData.licenseType) {
                searchParams.set("LicenseType", filterData.licenseType);
                hasChanges = true;
            }
        } else {
            if (currentLicenseType !== '') {
                searchParams.delete("LicenseType");
                hasChanges = true;
            }
        }

        // Sync licenseStatus
        const currentLicenseStatus = searchParams.get("LicenseStatus") || '';
        if (filterData.licenseStatus && filterData.licenseStatus !== '') {
            if (currentLicenseStatus !== filterData.licenseStatus) {
                searchParams.set("LicenseStatus", filterData.licenseStatus);
                hasChanges = true;
            }
        } else {
            if (currentLicenseStatus !== '') {
                searchParams.delete("LicenseStatus");
                hasChanges = true;
            }
        }

        // Sync followed companies; omit the parameter when unchecked.
        if (filterData.followedByMe) {
            if (searchParams.get('followedByMe') !== 'true') {
                searchParams.set('followedByMe', 'true');
                hasChanges = true;
            }
        } else if (searchParams.has('followedByMe')) {
            searchParams.delete('followedByMe');
            hasChanges = true;
        }

        // Sync page number
        const currentPage = pageNumber !== null ? pageNumber : page;
        const currentPageParam = searchParams.get("Page");
        const newPageParam = currentPage >= 0 ? (currentPage + 1).toString() : null;
        if (currentPageParam !== newPageParam) {
            if (newPageParam) {
                searchParams.set("Page", newPageParam);
            } else {
                searchParams.delete("Page");
            }
            hasChanges = true;
        }

        // Always update URL if there are changes, or if we need to ensure Search/Commodity params are removed
        // (This handles the case where params need to be deleted but no other changes were detected)
        const needsSearchDeletion = !preserveFilters &&
            filterData.search === '' &&
            searchParams.has("Search");
        const needsCommodityDeletion = (filterData.commodity === '0' || !filterData.commodity || filterData.commodity === '') &&
            searchParams.has("Commodity");

        if (hasChanges || needsSearchDeletion || needsCommodityDeletion) {
            // If we need to delete Search but it wasn't detected as a change, delete it now
            if (needsSearchDeletion && !hasChanges) {
                searchParams.delete("Search");
            }
            // If we need to delete Commodity but it wasn't detected as a change, delete it now
            if (needsCommodityDeletion && !hasChanges) {
                searchParams.delete("Commodity");
            }

            isSyncingRef.current = true;
            navigate(
                {
                    pathname: location.pathname,
                    search: searchParams.toString(),
                },
                { replace: true }
            );
            // Reset flag after URL update (give it time for location.search to update)
            setTimeout(() => {
                isSyncingRef.current = false;
            }, 100);
        }
    }
    const onFilterApply = async (data) => {
        // Mark that we're applying filters to prevent sync effect from interfering
        isApplyingFiltersRef.current = true;

        // Update filters state - this will trigger the fetch effect
        setFilters(data);
        setSearch(data.search?.trim() || '');
        // Reset to page 1 when filters change
        setPage(0);
        // Sync to query params (preserveFilters = false because this is a filter change, should clear if empty)
        // Don't make API call here - let the fetch effect handle it after state updates
        syncFiltersToQueryParams(data, 0, false);
        // Reset flag after a brief delay to allow state updates to complete
        setTimeout(() => {
            isApplyingFiltersRef.current = false;
        }, 100);
    }

    // Sync filters from query params when URL changes (e.g., direct URL change or browser back/forward)
    useEffect(() => {
        // Skip if we're the ones updating the URL (to prevent infinite loops)
        // Also skip if we're applying filters (onFilterApply handles the sync)
        if (isSyncingRef.current || isApplyingFiltersRef.current) {
            return;
        }

        const currentSearchParams = new URLSearchParams(location.search);
        // Map query param display values back to internal values
        const commodityParam = currentSearchParams.get("Commodity") || currentSearchParams.get("commodity") || '';
        let commodity = '0'; // Default to '0' (Both)
        if (commodityParam === 'Electric') {
            commodity = '1';
        } else if (commodityParam === 'Gas') {
            commodity = '2';
        }

        const queryFilters = {
            commodity,
            search: currentSearchParams.get("Search") || currentSearchParams.get("search") || '',
            state: currentSearchParams.get("StateCode") || currentSearchParams.get("stateCode") || '',
            licenseType: currentSearchParams.get("LicenseType") || currentSearchParams.get("licenseType") || '',
            licenseStatus: currentSearchParams.get("LicenseStatus") || currentSearchParams.get("licenseStatus") || '',
            followedByMe: currentSearchParams.get('followedByMe') === 'true'
        };

        // Check if filters have changed (only filter values, not page)
        const filtersChanged =
            filters.commodity !== queryFilters.commodity ||
            filters.search !== queryFilters.search ||
            filters.state !== queryFilters.state ||
            filters.licenseType !== queryFilters.licenseType ||
            filters.licenseStatus !== queryFilters.licenseStatus ||
            filters.followedByMe !== queryFilters.followedByMe;

        // Only update filters if they actually changed AND are present in URL
        // This prevents filters from being reset when only page changes
        const hasFiltersInUrl =
            currentSearchParams.has("Commodity") ||
            currentSearchParams.has("Search") ||
            currentSearchParams.has("StateCode") ||
            currentSearchParams.has("LicenseType") ||
            currentSearchParams.has("LicenseStatus") ||
            currentSearchParams.has('followedByMe');

        if (filtersChanged && (hasFiltersInUrl || filters.followedByMe !== queryFilters.followedByMe)) {
            setFilters(queryFilters);
            setSearch(queryFilters.search);
        } else if (queryFilters.search !== search) {
            // Even if other filters haven't changed, update search if it changed in URL
            // This handles the case where only Search query param is changed
            setSearch(queryFilters.search);
            // Also update filters.search to keep them in sync
            setFilters({ ...filters, search: queryFilters.search });
        }

        // Update page from query params (only if it's different)
        const queryPage = currentSearchParams.get("Page") || currentSearchParams.get("page");
        if (queryPage) {
            const pageNum = parseInt(queryPage) - 1;
            if (pageNum !== page && pageNum >= 0) {
                setPage(pageNum);
            }
            else {
                setPage(0);
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    // Track previous values to detect what actually changed
    const prevPageRef = useRef(page);
    const prevFiltersRef = useRef({ ...filters });

    // Sync filters to query params when page or filters change
    useEffect(() => {
        // Skip if we're applying filters (onFilterApply handles the sync)
        if (isApplyingFiltersRef.current) {
            prevPageRef.current = page;
            prevFiltersRef.current = { ...filters };
            return;
        }

        const pageChanged = prevPageRef.current !== page;
        const filtersChanged =
            prevFiltersRef.current.commodity !== filters.commodity ||
            prevFiltersRef.current.search !== filters.search ||
            prevFiltersRef.current.state !== filters.state ||
            prevFiltersRef.current.licenseType !== filters.licenseType ||
            prevFiltersRef.current.licenseStatus !== filters.licenseStatus ||
            prevFiltersRef.current.followedByMe !== filters.followedByMe;

        // Only sync if something actually changed
        if (pageChanged || filtersChanged) {
            // Always read current filters from URL to ensure we have the latest values
            // This is especially important when only page changes - we need to preserve all filters from URL
            const currentSearchParams = new URLSearchParams(location.search);
            const filtersToSync = { ...filters };
            let filtersUpdated = false;

            // Always sync search from URL if it exists (preserve it on page change)
            const urlSearch = currentSearchParams.get("Search") || '';
            if (urlSearch && filtersToSync.search !== urlSearch) {
                filtersToSync.search = urlSearch;
                filtersUpdated = true;
            }
            // Always sync state from URL if it exists
            const urlState = currentSearchParams.get("StateCode") || '';
            if (urlState && filtersToSync.state !== urlState) {
                filtersToSync.state = urlState;
                filtersUpdated = true;
            }
            // Always sync licenseType from URL if it exists
            const urlLicenseType = currentSearchParams.get("LicenseType") || '';
            if (urlLicenseType && filtersToSync.licenseType !== urlLicenseType) {
                filtersToSync.licenseType = urlLicenseType;
                filtersUpdated = true;
            }
            // Always sync licenseStatus from URL if it exists
            const urlLicenseStatus = currentSearchParams.get("LicenseStatus") || '';
            if (urlLicenseStatus && filtersToSync.licenseStatus !== urlLicenseStatus) {
                filtersToSync.licenseStatus = urlLicenseStatus;
                filtersUpdated = true;
            }
            // Always sync commodity from URL - map display values to internal values
            const urlCommodityParam = currentSearchParams.get("Commodity") || '';
            let urlCommodity = '0'; // Default to '0' (Both)
            if (urlCommodityParam === 'Electric') {
                urlCommodity = '1';
            } else if (urlCommodityParam === 'Gas') {
                urlCommodity = '2';
            }
            if (urlCommodity !== '0' && filtersToSync.commodity !== urlCommodity) {
                filtersToSync.commodity = urlCommodity;
                filtersUpdated = true;
            }

            // If we updated filters from URL, update the filters state and search state
            // This ensures filters state always matches what's in the URL
            if (filtersUpdated) {
                setFilters(filtersToSync);
                if (filtersToSync.search) {
                    setSearch(filtersToSync.search);
                }
            }

            // When only page changes, preserve filters from URL (preserveFilters = true)
            // When filters change, don't preserve (preserveFilters = false)
            const isOnlyPageChange = pageChanged && !filtersChanged;
            syncFiltersToQueryParams(filtersToSync, page, isOnlyPageChange);
            prevPageRef.current = page;
            prevFiltersRef.current = { ...filtersToSync };
        }
    }, [page, filters.commodity, filters.search, filters.state, filters.licenseType, filters.licenseStatus, filters.followedByMe]);

    // Fetch data when filters or page change
    useEffect(() => {
        const fetchdata = async () => {
            setLoadingCompanyData(true);
            // Use filters.search as primary source since it's synced with query params
            // Fallback to search state for backward compatibility
            const searchTerm = filters.search || search || '';
            const companyDataResponse = await rpost(
                `${DEV_URL}/api/company/getall`,
                {
                    PageNumber: page + 1,
                    PageSize: limit,
                    SearchTerm: searchTerm,
                    orderBy: orderBy === undefined ? null : orderBy,
                    ascendingOrder,
                    stateCode: filters.state || '',
                    licenseType: filters.licenseType || '',
                    licenseStatus: filters.licenseStatus || '',
                    commodity: filters.commodity || '0',
                    followedByMe: filters.followedByMe === true
                }
            );
            const response = await rget(
                `${DEV_URL}/api/address/GetStates`
            );
            if (response !== 'Data not found') {
                response.data.splice(0, 0, { id: '', name: 'All', code: '' })
                setExtra({ stateList: response.data, onApply: onFilterApply, setCompanyData: setCompanyData });
            }
            setTotalRecords(`${companyDataResponse?.data?.totalRecords} records`);
            setCompanyData(companyDataResponse?.data?.company);
            setCompanyDataCount(companyDataResponse?.data?.totalRecords);
            setLoadingCompanyData(false);

            return () => {
                setCompanyData();
                setCompanyDataCount();
            };
        }
        fetchdata();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [limit, page, search, orderBy, ascendingOrder, filters]);

    return (
        <>
            <Div className="d-flex align-items-center justify-content-between flex-wrap company-page-header">
                <div className="d-flex align-items-center flex-wrap company-page-title-row">
                    <PageTitle title={`Companies`} />
                    <Text variant="subtitle" className="ms-md-2 company-page-total-records" style={{ marginTop: "16px" }}>{totalRecords}</Text>
                </div>
                {/* <Button className="btn btn-primary mb- fs-11">
          Create Company
        </Button> */}
            </Div>
            <Row>
                <Col lg={12}>
                    <Card>
                        <Card.Body>
                            <Row>
                                <Col sm="12">
                                    {companyData && (
                                        <CompaniesDataTable
                                            companies={companyData}
                                            loading={loadingCompanyData}
                                            total={companyDataCount}
                                            hook={hook}
                                            filters={CompanyFilters}
                                            extra={extra}
                                        />
                                    )}
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </>
    );
};

export default CompaniesData;
